import React, { useCallback, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuditWorkspace } from "./useAuditWorkspace";
import DraftStatus from "./DraftStatus";
import ExitTaskModal from "./ExitTaskModal";
import FieldReviewTable from "./FieldReviewTable";
import Panel from "./Panel";
import MediaGallery from "./MediaGallery";
import {
  commonIssueField,
  hasCorrectionValue,
  hasIncorrectDecision,
  isCorrectionNoteMissing,
} from "../shared/helpers";
import {
  wsContainer,
  wsBreadcrumb,
  wsBreadcrumbLink,
  wsBreadcrumbSep,
  wsHeader,
  wsHeaderLeft,
  wsHeaderRight,
  wsNavBtn,
  wsTitle,
  wsCounter,
  wsBody,
  wsLeftCol,
  wsRightCol,
  wsMediaPlaceholder,
  wsErrorSummary,
  wsProgressTrack,
  wsProgressFill,
  wsSuccessBanner,
  checklistBtn,
} from "./workspaceStyles";
import {
  COMPARISON_MODE,
  MEDIA_FIELD_TYPES,
  ChecklistResponseValue,
  FieldCommon,
  FieldDecision,
  FormConfigField,
} from "../shared/types";
import * as S from "../shared/styles";
import Loader from "@amzn/meridian/loader";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";

// ----------------------------  Helpers  ----------------------------

function isMediaField(type: string): boolean {
  return (MEDIA_FIELD_TYPES as readonly string[]).indexOf(type) !== -1;
}

function validateFields(
  fields: FormConfigField[],
  decisions: Record<string, FieldDecision>,
  fieldCommon: FieldCommon,
): string[] {
  const errors: string[] = [];
  fields.forEach((f) => {
    if (f.mandatory && !decisions[f.label]?.decision) {
      errors.push(f.label);
    }
  });
  // One shared Issue Category / Details for the review when any field is incorrect.
  const issueField = commonIssueField(fields);
  if (issueField && hasIncorrectDecision(decisions)) {
    if (!hasCorrectionValue(issueField, fieldCommon.issue_category)) errors.push("Issue category");
    if (isCorrectionNoteMissing(issueField, fieldCommon.issue_category, fieldCommon.explanation))
      errors.push("Details");
  }
  return errors;
}

// Stable fallback so memo/callback deps don't change on every render.
const EMPTY_FIELD_COMMON: FieldCommon = {};

// ----------------------------  Main Component  ----------------------------

const AuditWorkspace: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();

  const {
    task,
    config,
    result,
    allTasks,
    isLoading,
    isError,
    isSaving,
    saveSuccess,
    saveError,
    setDecisions,
    setFieldCommon,
    setChecklist,
    setComments,
    saveDraft,
    submitResult,
    resetSave,
  } = useAuditWorkspace(taskId);

  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [dirty, setDirty] = useState(false);
  const [pendingExit, setPendingExit] = useState(false);
  const errorSummaryRef = useRef<HTMLDivElement>(null);

  // Reset per-task UI state when navigating between tasks.
  React.useEffect(() => {
    setAttemptedSubmit(false);
    setExitOpen(false);
    setLastSavedAt(null);
    setDirty(false);
    setPendingExit(false);
  }, [taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  const decisions = result?.decisions ?? {};
  const fieldCommon = result?.field_common ?? EMPTY_FIELD_COMMON;
  const checklist = result?.checklist_responses ?? {};
  const comments = result?.comments ?? "";
  const submitted = result?.status === "submitted";

  const effectiveMode = task?.comparison_mode ?? config?.comparison_mode;
  const isOverride = effectiveMode === COMPARISON_MODE.OVERRIDE;

  const inputFields = useMemo(
    () =>
      (config?.fields ?? [])
        .filter((f) => !isMediaField(f.type))
        .sort((a, b) => a.display_order - b.display_order),
    [config],
  );

  const mediaFields = useMemo(
    () => (config?.fields ?? []).filter((f) => isMediaField(f.type)),
    [config],
  );

  const fieldErrors = useMemo(
    () => validateFields(inputFields, decisions, fieldCommon),
    [inputFields, decisions, fieldCommon],
  );

  const checklistItems = useMemo(
    () => (config?.checklist_items ?? []).slice().sort((a, b) => a.display_order - b.display_order),
    [config],
  );

  const markedCount = checklistItems.filter((i) => checklist[i.id]).length;
  const checklistPct = checklistItems.length
    ? Math.round((markedCount / checklistItems.length) * 100)
    : 100;
  const unmarkedItems = checklistItems.filter((i) => !checklist[i.id]);
  const canSubmit = fieldErrors.length === 0 && unmarkedItems.length === 0;

  // Progress counter & last-task detection — scoped to same use case
  const sameCaseTasks = allTasks.filter((t) => t.use_case_id === task?.use_case_id);
  const sameCaseCompleted = sameCaseTasks.filter(
    (t) => t.status === "completed" || t.status === "expired",
  ).length;
  const counter = `${sameCaseCompleted} / ${sameCaseTasks.length}`;
  const sameCaseRemaining = sameCaseTasks.filter(
    (t) => t.id !== task?.id && t.status !== "completed" && t.status !== "expired",
  ).length;
  const isLastTask = sameCaseRemaining === 0;
  const incidentId = task?.id ?? "";

  // Prev / Next navigation within same use case (non-completed only)
  const sameCaseActive = sameCaseTasks.filter(
    (t) => t.status !== "completed" && t.status !== "expired",
  );
  const activeIdx = sameCaseActive.findIndex((t) => t.id === task?.id);
  const prevTask =
    activeIdx > 0
      ? sameCaseActive[activeIdx - 1]
      : sameCaseActive.length > 1
        ? sameCaseActive[sameCaseActive.length - 1]
        : null;
  const nextTask =
    activeIdx < sameCaseActive.length - 1
      ? sameCaseActive[activeIdx + 1]
      : sameCaseActive.length > 1
        ? sameCaseActive[0]
        : null;

  // Handle save success — update last-saved timestamp, or navigate if exiting
  React.useEffect(() => {
    if (saveSuccess && result?.status === "draft") {
      resetSave();
      if (pendingExit) {
        setPendingExit(false);
        navigate("/vlmAudit/my-tasks");
        return;
      }
      setLastSavedAt(new Date());
      setDirty(false);
    }
  }, [saveSuccess, result?.status, resetSave, pendingExit, navigate]);

  // Handle submit success — navigate to next task in same use case, or back to queue
  React.useEffect(() => {
    if (saveSuccess && result?.status === "submitted") {
      resetSave();
      const useCaseId = task?.use_case_id;
      const sameCasePool = allTasks.filter((t) => t.use_case_id === useCaseId);
      const currentIdx = sameCasePool.findIndex((t) => t.id === task?.id);
      const ordered =
        currentIdx >= 0
          ? [...sameCasePool.slice(currentIdx + 1), ...sameCasePool.slice(0, currentIdx)]
          : sameCasePool;
      const next = ordered.find((t) => t.status !== "completed" && t.status !== "expired");
      if (next) {
        navigate("/vlmAudit/workspace/" + next.id);
      } else {
        navigate("/vlmAudit/my-tasks");
      }
    }
  }, [saveSuccess, result?.status, allTasks, task?.id, task?.use_case_id, navigate, resetSave]);

  // Auto-dismiss save error after 5 seconds so the banner does not persist indefinitely.
  React.useEffect(() => {
    if (!saveError) return;
    setPendingExit(false);
    const timer = setTimeout(() => resetSave(), 5000);
    return () => clearTimeout(timer);
  }, [saveError, resetSave]);

  const setDecision = useCallback(
    (label: string, patch: Partial<FieldDecision>) => {
      const next = { ...decisions, [label]: { ...(decisions[label] || {}), ...patch } };
      setDecisions(next);
      // The shared correction only applies while some field is incorrect.
      if (!hasIncorrectDecision(next) && Object.keys(fieldCommon).length > 0) setFieldCommon({});
      setDirty(true);
    },
    [decisions, setDecisions, fieldCommon, setFieldCommon],
  );

  const updateFieldCommon = useCallback(
    (patch: Partial<FieldCommon>) => {
      setFieldCommon({ ...fieldCommon, ...patch });
      setDirty(true);
    },
    [fieldCommon, setFieldCommon],
  );

  const setChecklistItem = useCallback(
    (itemId: string, response: ChecklistResponseValue) => {
      const next = { ...checklist, [itemId]: response };
      setChecklist(next);
      setDirty(true);
    },
    [checklist, setChecklist],
  );

  const handleCommentsChange = useCallback(
    (value: string) => {
      setComments(value);
      setDirty(true);
    },
    [setComments],
  );

  const handleSubmit = () => {
    setAttemptedSubmit(true);
    if (!canSubmit) {
      setTimeout(
        () => errorSummaryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }),
        0,
      );
      return;
    }
    submitResult();
  };

  const requestExit = () => {
    if (submitted) {
      navigate("/vlmAudit/my-tasks");
      return;
    }
    setExitOpen(true);
  };

  const saveDraftAndExit = () => {
    setExitOpen(false);
    if (!result) {
      navigate("/vlmAudit/my-tasks");
      return;
    }
    setPendingExit(true);
    saveDraft();
  };

  const discardAndExit = () => {
    setExitOpen(false);
    navigate("/vlmAudit/my-tasks");
  };

  // ----------------------------  Loading / Error / Not Found  ----------------------------

  if (isLoading) {
    return (
      <Row width="fill" alignmentHorizontal="center">
        <Column height="60vh" alignmentVertical="center">
          <Loader />
        </Column>
      </Row>
    );
  }

  if (isError) {
    return (
      <div style={S.pageWrapper}>
        <div
          style={{
            ...S.devBanner,
            background: "#fee2e2",
            borderColor: "#ef4444",
            color: "#991b1b",
          }}
        >
          Failed to load workspace task.
        </div>
        <button style={S.btnSecondary} onClick={() => navigate("/vlmAudit/my-tasks")}>
          Back to Queue
        </button>
      </div>
    );
  }

  if (!task || !config) {
    return (
      <div style={S.pageWrapper}>
        <div
          style={{
            ...S.devBanner,
            background: "#fee2e2",
            borderColor: "#ef4444",
            color: "#991b1b",
          }}
        >
          Task not found or has no active form configuration.
        </div>
        <button style={S.btnSecondary} onClick={() => navigate("/vlmAudit/my-tasks")}>
          Back to Queue
        </button>
      </div>
    );
  }

  // ----------------------------  Render  ----------------------------

  return (
    <div style={wsContainer}>
      {/* Breadcrumb */}
      <div style={wsBreadcrumb}>
        <button style={wsBreadcrumbLink} onClick={requestExit}>
          Excavator
        </button>
        <span style={wsBreadcrumbSep}>{"\u203A"}</span>
        <span style={wsBreadcrumbLink}>{config.name}</span>
        <span style={wsBreadcrumbSep}>{"\u203A"}</span>
        <span style={{ ...wsBreadcrumbLink, color: "#4b5563", fontWeight: 500 }}>
          {task.week || "Queue"}
        </span>
      </div>

      {/* Incident Header */}
      <div style={wsHeader}>
        <div style={wsHeaderLeft}>
          <button
            style={{ ...wsNavBtn, opacity: prevTask ? 1 : 0.35 }}
            disabled={!prevTask}
            onClick={() => prevTask && navigate("/vlmAudit/workspace/" + prevTask.id)}
            title="Previous task"
          >
            {"\u2039"}
          </button>
          <button
            style={{ ...wsNavBtn, opacity: nextTask ? 1 : 0.35 }}
            disabled={!nextTask}
            onClick={() => nextTask && navigate("/vlmAudit/workspace/" + nextTask.id)}
            title="Next task"
          >
            {"\u203A"}
          </button>
          <h1 style={wsTitle}>Incident ID: {incidentId}</h1>
        </div>
        <div style={wsHeaderRight}>
          <DraftStatus dirty={dirty} lastSavedAt={lastSavedAt} submitted={submitted} />
          <span style={wsCounter}>{counter}</span>
          <button
            style={{
              ...S.btnOutline,
              padding: "10px 20px",
              fontSize: "1rem",
              opacity: submitted ? 0.5 : 1,
            }}
            disabled={submitted || isSaving}
            onClick={saveDraft}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
          <button
            style={{
              ...S.btnPrimary,
              padding: "10px 20px",
              fontSize: "1rem",
              opacity: submitted ? 0.5 : 1,
            }}
            disabled={submitted || isSaving}
            onClick={handleSubmit}
          >
            {isSaving ? "Saving..." : isLastTask ? "Submit & Finish" : "Submit & Next"}
          </button>
          <button
            style={{ ...S.btnSecondary, padding: "10px 20px", fontSize: "1rem" }}
            onClick={requestExit}
          >
            Exit
          </button>
        </div>
      </div>

      {/* Main Content — 5/12 left, 7/12 right */}
      <div style={wsBody}>
        {/* Left: Incident description + media */}
        <div style={wsLeftCol}>
          {task.incident_description && (
            <Panel title="Incident Description">
              <p style={{ fontSize: "1.125rem", color: "#374151", lineHeight: 1.6 }}>
                {task.incident_description}
              </p>
            </Panel>
          )}
          <Panel title="Media Viewer">
            {(task.media_urls?.length ?? 0) > 0 ? (
              <MediaGallery media={task.media_urls!} />
            ) : mediaFields.length > 0 ? (
              <div style={wsMediaPlaceholder}>
                {mediaFields.map((f) => f.label).join(", ")} — no media attached
              </div>
            ) : (
              <div style={wsMediaPlaceholder}>Text-only use case</div>
            )}
          </Panel>
        </div>

        {/* Right: Fields + Checklist + Comments */}
        <div style={wsRightCol}>
          {/* Submit error */}
          {saveError && (
            <div
              style={{
                ...S.devBanner,
                background: "#fee2e2",
                borderColor: "#ef4444",
                color: "#991b1b",
                marginBottom: "16px",
              }}
            >
              Failed to save. Please try again.
            </div>
          )}

          {/* Validation error summary */}
          {attemptedSubmit && !canSubmit && (
            <div ref={errorSummaryRef} style={wsErrorSummary}>
              <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                Please resolve before submitting:
              </div>
              <ul style={{ margin: 0, paddingLeft: "20px", fontSize: "1rem" }}>
                {fieldErrors.map((e) => (
                  <li key={e}>Needs a decision/correction: {e}</li>
                ))}
                {unmarkedItems.length > 0 && (
                  <li>
                    Checklist incomplete — mark: {unmarkedItems.map((i) => i.label).join(", ")}
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Field review table */}
          <Panel title={config.name}>
            {inputFields.length === 0 ? (
              <p style={{ fontSize: "1.125rem", color: "#9ca3af" }}>
                No decision fields configured.
              </p>
            ) : (
              <FieldReviewTable
                fields={inputFields}
                isOverride={isOverride}
                task={task}
                decisions={decisions}
                onDecide={setDecision}
                fieldCommon={fieldCommon}
                onCommonChange={updateFieldCommon}
                showError={attemptedSubmit}
                disabled={submitted}
              />
            )}
          </Panel>

          {/* Checklist */}
          <Panel title="Checklist">
            {checklistItems.length > 0 ? (
              <>
                {/* Progress bar */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "12px",
                  }}
                >
                  <div style={wsProgressTrack}>
                    <div
                      style={{
                        ...wsProgressFill,
                        width: checklistPct + "%",
                        background: checklistPct === 100 ? "#22c55e" : "#f59e0b",
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: "1rem",
                      fontWeight: 500,
                      color: "#6b7280",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {markedCount}/{checklistItems.length} · {checklistPct}%
                  </span>
                </div>
                {/* Items */}
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {checklistItems.map((item) => {
                    const unmarkedHighlighted = attemptedSubmit && !checklist[item.id];
                    return (
                      <div
                        key={item.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "1.125rem",
                          background: unmarkedHighlighted ? "#fee2e2" : "transparent",
                        }}
                      >
                        <span style={{ color: "#374151" }}>{item.label}</span>
                        <div style={{ display: "flex", gap: "4px" }}>
                          {(["pass", "fail", "na"] as const).map((r) => (
                            <button
                              key={r}
                              disabled={submitted}
                              onClick={() => setChecklistItem(item.id, r)}
                              style={checklistBtn(checklist[item.id] === r, r, submitted)}
                            >
                              {r === "na" ? "N/A" : r.charAt(0).toUpperCase() + r.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <p style={{ fontSize: "1.125rem", color: "#9ca3af" }}>No checklist configured.</p>
            )}
          </Panel>

          {/* Comments */}
          <Panel title="Comments">
            <textarea
              rows={3}
              value={comments}
              onChange={(e) => handleCommentsChange(e.target.value)}
              placeholder="Optional reviewer comments..."
              style={S.formTextarea}
              disabled={submitted}
            />
          </Panel>
        </div>
      </div>

      {/* Status Banner */}
      {submitted && <div style={wsSuccessBanner}>This result has been submitted.</div>}

      <ExitTaskModal
        open={exitOpen}
        hasDraft={dirty || (result?.status === "draft" && Object.keys(result.decisions).length > 0)}
        onSaveAndExit={saveDraftAndExit}
        onDiscardAndExit={discardAndExit}
        onCancel={() => setExitOpen(false)}
      />
    </div>
  );
};

export default AuditWorkspace;
